import { useState } from 'react'
import { Wifi, Server, Camera, Lock, Zap, Thermometer, Smartphone, Clock, AlertCircle, Power, Activity, Settings } from 'lucide-react'

export default function IoTDashboardPage() {
  const [stats] = useState({
    totalDevices: 847,
    devicesOnline: 823,
    uptime: '99.94%',
    alerts: 12,
  })

  const deviceCategories = [
    {
      name: 'Servers & Infrastructure',
      icon: '🖥️',
      color: 'from-blue-600 to-blue-500',
      devices: [
        { name: 'Primary Web Server', status: 'Online', uptime: '99.99%', cpu: '45%', memory: '72%', temp: '52°C', location: 'Data Center A' },
        { name: 'Secondary Web Server', status: 'Online', uptime: '99.98%', cpu: '38%', memory: '65%', temp: '49°C', location: 'Data Center A' },
        { name: 'Database Server', status: 'Online', uptime: '99.97%', cpu: '82%', memory: '88%', temp: '61°C', location: 'Data Center A' },
        { name: 'Backup Server', status: 'Online', uptime: '99.99%', cpu: '12%', memory: '34%', temp: '45°C', location: 'Data Center B' },
        { name: 'Mail Server', status: 'Online', uptime: '99.98%', cpu: '28%', memory: '56%', temp: '48°C', location: 'Data Center A' },
      ]
    },
    {
      name: 'Network Equipment',
      icon: '🌐',
      color: 'from-cyan-600 to-cyan-500',
      devices: [
        { name: 'Main Router (R1)', status: 'Online', uptime: '99.99%', bandwidth: '2.4 Gbps', latency: '2ms', temp: '38°C', location: 'Network Room' },
        { name: 'Secondary Router (R2)', status: 'Online', uptime: '99.97%', bandwidth: '1.8 Gbps', latency: '3ms', temp: '36°C', location: 'Network Room' },
        { name: 'Core Switch (S1)', status: 'Online', uptime: '99.99%', ports: '48/48 Active', bandwidth: '10 Gbps', temp: '41°C', location: 'Network Room' },
        { name: 'Firewall (FW1)', status: 'Online', uptime: '99.98%', packets: '4.2M/min', threats: '847 Blocked', temp: '44°C', location: 'Network Room' },
        { name: 'Access Point (AP1)', status: 'Online', uptime: '99.95%', clients: '234', signal: '-45dBm', temp: '35°C', location: 'Floor 1' },
      ]
    },
    {
      name: 'Security & Access',
      icon: '🔒',
      color: 'from-purple-600 to-purple-500',
      devices: [
        { name: 'Security Camera (Front)', status: 'Online', uptime: '99.99%', resolution: '4K', storage: '287GB/500GB', temp: '32°C', location: 'Front Entrance' },
        { name: 'Security Camera (Rear)', status: 'Online', uptime: '99.98%', resolution: '4K', storage: '289GB/500GB', temp: '28°C', location: 'Rear Exit' },
        { name: 'Security Camera (Server Room)', status: 'Online', uptime: '99.99%', resolution: '2K', storage: '156GB/256GB', temp: '40°C', location: 'Data Center A' },
        { name: 'Door Lock System (Main)', status: 'Online', uptime: '99.97%', access: '1247 Today', battery: '87%', temp: '22°C', location: 'Main Entrance' },
        { name: 'Biometric Scanner', status: 'Online', uptime: '99.96%', users: '342', failed: '3', temp: '24°C', location: 'Secure Area' },
      ]
    },
    {
      name: 'Office Equipment',
      icon: '🖨️',
      color: 'from-orange-600 to-orange-500',
      devices: [
        { name: 'Printer (Floor 1)', status: 'Online', uptime: '98.92%', pages: '47283', toner: '45%', temp: '38°C', location: 'Floor 1' },
        { name: 'Copier (Floor 2)', status: 'Online', uptime: '97.84%', pages: '89234', toner: '23%', temp: '42°C', location: 'Floor 2' },
        { name: 'Scanner (Finance)', status: 'Online', uptime: '99.11%', scans: '2847', ink: '67%', temp: '31°C', location: 'Finance Dept' },
        { name: 'VoIP Phone System', status: 'Online', uptime: '99.99%', calls: '1847', active: '12', temp: '33°C', location: 'Comms Room' },
      ]
    },
    {
      name: 'Environmental Control',
      icon: '🌡️',
      color: 'from-green-600 to-green-500',
      devices: [
        { name: 'HVAC System (Main)', status: 'Online', uptime: '99.87%', temp: '21.5°C', humidity: '45%', mode: 'Cool', location: 'Building 1' },
        { name: 'Temperature Sensor (DC-A)', status: 'Online', uptime: '99.99%', temp: '19.8°C', humidity: '42%', alert: 'Normal', location: 'Data Center A' },
        { name: 'Temperature Sensor (DC-B)', status: 'Online', uptime: '99.99%', temp: '20.2°C', humidity: '43%', alert: 'Normal', location: 'Data Center B' },
        { name: 'Power Management (UPS)', status: 'Online', uptime: '99.99%', load: '34%', battery: '100%', temp: '28°C', location: 'Main Facility' },
      ]
    },
    {
      name: 'Mobile & Wearables',
      icon: '📱',
      color: 'from-pink-600 to-pink-500',
      devices: [
        { name: 'Company Phones', status: 'Online', uptime: '99.82%', devices: '234', active: '198', battery: 'Avg 76%', location: 'Various' },
        { name: 'Tablets', status: 'Online', uptime: '99.91%', devices: '87', active: '62', battery: 'Avg 82%', location: 'Various' },
        { name: 'Smart Watches', status: 'Online', uptime: '99.74%', devices: '45', active: '34', battery: 'Avg 68%', location: 'Various' },
        { name: 'GPS Trackers', status: 'Online', uptime: '99.89%', devices: '28', active: '23', battery: 'Avg 74%', location: 'Fleet' },
      ]
    },
  ]

  const alertsList = [
    { device: 'Printer (Floor 1)', alert: 'Low toner warning', severity: 'Low', time: '2 min ago' },
    { device: 'HVAC System', alert: 'Temperature slightly elevated', severity: 'Medium', time: '15 min ago' },
    { device: 'Copier (Floor 2)', alert: 'Maintenance reminder', severity: 'Low', time: '1 hour ago' },
    { device: 'Database Server', alert: 'High CPU usage (82%)', severity: 'Medium', time: '3 hours ago' },
    { device: 'Access Point (AP1)', alert: 'Low signal areas detected', severity: 'Low', time: '5 hours ago' },
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-blue-950 to-slate-900 p-8">
      <div className="space-y-6">
        {/* Header */}
        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-emerald-600 via-teal-600 to-cyan-600 p-8 text-white shadow-2xl border border-emerald-500/20">
          <div className="relative z-10">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center space-x-3 mb-2">
                  <Wifi className="w-8 h-8 text-yellow-300" />
                  <h1 className="text-5xl font-black">IoT Management Dashboard</h1>
                </div>
                <p className="text-emerald-100 text-lg font-semibold">Physical Device Monitoring & Control</p>
                <p className="text-emerald-200 text-sm mt-1">Servers • Network • Security • Office Equipment • Environmental • Mobile Devices</p>
              </div>
              <div className="text-right">
                <div className="text-4xl font-black text-yellow-300">{stats.devicesOnline}/{stats.totalDevices}</div>
                <div className="text-sm text-emerald-100">Devices Online</div>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[
            { label: 'Total Devices', value: stats.totalDevices, icon: Wifi, color: 'from-blue-600 to-blue-500' },
            { label: 'Devices Online', value: stats.devicesOnline, icon: Activity, color: 'from-green-600 to-green-500' },
            { label: 'Average Uptime', value: stats.uptime, icon: Clock, color: 'from-purple-600 to-purple-500' },
            { label: 'Active Alerts', value: stats.alerts, icon: AlertCircle, color: 'from-orange-600 to-orange-500' },
          ].map((stat, idx) => (
            <div key={idx} className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
              <p className="text-blue-300 text-sm font-semibold">{stat.label}</p>
              <p className="text-white text-3xl font-black mt-3">{stat.value}</p>
            </div>
          ))}
        </div>

        {/* Device Categories */}
        <div className="space-y-6">
          {deviceCategories.map((category, cidx) => (
            <div key={cidx}>
              <h2 className="text-3xl font-bold text-white mb-6 flex items-center gap-3">
                <span className="text-3xl">{category.icon}</span>
                {category.name}
              </h2>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {category.devices.map((device, didx) => (
                  <div key={didx} className={`relative overflow-hidden rounded-xl bg-gradient-to-br from-slate-800 to-slate-900 border border-blue-500/20 p-6 hover:border-blue-400/40 transition-all`}>
                    <div className={`h-1 w-full bg-gradient-to-r ${category.color} rounded-full mb-4 shadow-lg`}></div>

                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="text-lg font-bold text-white">{device.name}</h3>
                        <p className="text-blue-300 text-sm">📍 {device.location}</p>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-bold ${
                        device.status === 'Online'
                          ? 'bg-green-500/20 text-green-300'
                          : 'bg-red-500/20 text-red-300'
                      }`}>
                        {device.status}
                      </span>
                    </div>

                    {/* Device Stats Grid */}
                    <div className="grid grid-cols-2 gap-3">
                      {Object.entries(device).map(([key, value]) => {
                        if (['name', 'status', 'location'].includes(key)) return null
                        return (
                          <div key={key} className="p-2 bg-slate-700/50 rounded border border-blue-500/20">
                            <p className="text-blue-300 text-xs font-semibold capitalize">{key}</p>
                            <p className="text-white text-sm font-bold mt-1">{value}</p>
                          </div>
                        )
                      })}
                    </div>

                    {/* Controls */}
                    <div className="flex gap-2 mt-4 pt-4 border-t border-blue-500/20">
                      <button className="flex-1 bg-blue-600/30 hover:bg-blue-600/50 px-3 py-2 rounded text-blue-300 hover:text-blue-100 text-xs font-bold transition-all">
                        📊 Details
                      </button>
                      <button className="flex-1 bg-green-600/30 hover:bg-green-600/50 px-3 py-2 rounded text-green-300 hover:text-green-100 text-xs font-bold transition-all">
                        ⚙️ Control
                      </button>
                      <button className="flex-1 bg-purple-600/30 hover:bg-purple-600/50 px-3 py-2 rounded text-purple-300 hover:text-purple-100 text-xs font-bold transition-all">
                        🔧 Config
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Active Alerts */}
        <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
          <h3 className="text-2xl font-bold text-white mb-6 flex items-center gap-2">
            <AlertCircle className="w-6 h-6 text-orange-400" />
            Active Device Alerts
          </h3>

          <div className="space-y-3">
            {alertsList.map((alert, idx) => (
              <div key={idx} className="flex items-center justify-between p-4 bg-slate-700/50 rounded-lg border border-blue-500/20 hover:border-blue-400/40 transition-all">
                <div className="flex-1">
                  <p className="text-white font-bold">{alert.device}</p>
                  <p className="text-blue-300 text-sm">{alert.alert}</p>
                </div>
                <div className="flex items-center gap-4">
                  <span className={`px-3 py-1 text-xs font-bold rounded-full ${
                    alert.severity === 'High' ? 'bg-red-500/20 text-red-300' :
                    alert.severity === 'Medium' ? 'bg-yellow-500/20 text-yellow-300' :
                    'bg-green-500/20 text-green-300'
                  }`}>
                    {alert.severity}
                  </span>
                  <p className="text-blue-400 text-xs">{alert.time}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* IoT Network Health */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Network Status */}
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
            <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Wifi className="w-5 h-5 text-cyan-400" />
              Network Health
            </h3>
            <div className="space-y-3">
              {[
                { metric: 'Overall Connectivity', value: '99.94%', status: 'Optimal' },
                { metric: 'Avg Response Time', value: '2.3ms', status: 'Excellent' },
                { metric: 'Packet Loss', value: '0.02%', status: 'Negligible' },
                { metric: 'Bandwidth Utilization', value: '34%', status: 'Healthy' },
                { metric: 'Device Communication', value: '847/847', status: 'All Online' },
              ].map((item, idx) => (
                <div key={idx} className="p-3 bg-slate-700/50 rounded border border-green-500/20">
                  <div className="flex justify-between items-center">
                    <p className="text-blue-300 text-sm font-semibold">{item.metric}</p>
                    <span className="text-green-400 font-bold text-sm">{item.value}</span>
                  </div>
                  <p className="text-green-500 text-xs mt-1">{item.status}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Maintenance Schedule */}
          <div className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl border border-blue-500/20 p-6">
            <h3 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Settings className="w-5 h-5 text-purple-400" />
              Maintenance Schedule
            </h3>
            <div className="space-y-3">
              {[
                { task: 'Copier (Floor 2) Servicing', date: 'Tomorrow 2:00 PM', status: 'Scheduled' },
                { task: 'Firewall Security Update', date: 'This Friday', status: 'Planned' },
                { task: 'Server Room AC Service', date: 'Next Monday', status: 'Planned' },
                { task: 'Network Cable Inspection', date: 'Next Week', status: 'Pending' },
                { task: 'Printer Cartridge Replacement', date: 'As Needed', status: 'On Demand' },
              ].map((item, idx) => (
                <div key={idx} className="p-3 bg-slate-700/50 rounded border border-purple-500/20">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-white text-sm font-bold">{item.task}</p>
                      <p className="text-blue-300 text-xs mt-1">📅 {item.date}</p>
                    </div>
                    <span className="text-purple-400 text-xs font-bold">{item.status}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
